import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useTelegram } from '@/hooks/useTelegram';
import { useAuth } from '@/hooks/useAuth';
import type { Item } from '@/types';

export function InventoryPage() {
  const navigate = useNavigate();
  const { hapticFeedback } = useTelegram();
  const { character } = useAuth();

  if (!character) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center safe-area-top safe-area-bottom">
        <div className="text-center p-4">
          <span className="text-4xl block mb-4">🎒</span>
          <p className="text-gray-400">Сначала создайте персонажа</p>
          <button
            onClick={() => navigate('/character-creation')}
            className="mt-4 px-4 py-2 bg-amber-500 text-black rounded-lg font-medium"
          >
            Создать персонажа
          </button>
        </div>
      </div>
    );
  }

  const inventory = character.inventory || [];
  const currentLoad = inventory.reduce((sum, item) => sum + (item.weight || 0), 0);
  const maxLoad = character.maxLoad || 10;
  const loadPercent = Math.min((currentLoad / maxLoad) * 100, 100);

  const getLoadColor = () => {
    if (loadPercent >= 100) return 'from-red-600 to-red-500';
    if (loadPercent >= 75) return 'from-amber-600 to-amber-500';
    return 'from-green-600 to-green-500';
  };

  const getItemIcon = (item: Item) => {
    const tags = item.tags || [];
    if (item.damage) return '⚔️';
    if (item.armor) return '🛡️';
    if (tags.includes('healing')) return '💊';
    if (tags.includes('potion')) return '🧪';
    if (tags.includes('ammo')) return '🏹';
    if (tags.includes('ration')) return '🍖';
    if (tags.includes('dungeon gear')) return '🔦';
    return '📦';
  };

  return (
    <div className="min-h-screen bg-gray-900 safe-area-top safe-area-bottom">
      {/* Header */}
      <div className="p-4 border-b border-gray-800 flex items-center justify-between">
        <button
          onClick={() => {
            hapticFeedback('light');
            navigate('/');
          }}
          className="text-gray-400 hover:text-white transition-colors"
        >
          ← Назад
        </button>
        <h1 className="text-lg font-bold text-white">Инвентарь</h1>
        <div className="w-16" />
      </div>

      {/* Load bar */}
      <div className="p-4 border-b border-gray-800 bg-gray-800/50">
        <div className="flex items-center justify-between text-sm mb-2">
          <span className="text-gray-400">Нагрузка</span>
          <span className={loadPercent >= 100 ? 'text-red-400' : 'text-gray-300'}>
            {currentLoad} / {maxLoad}
          </span>
        </div>
        <div className="h-2 bg-gray-700 rounded-full overflow-hidden">
          <motion.div
            initial={{ width: 0 }}
            animate={{ width: `${loadPercent}%` }}
            className={`h-full bg-gradient-to-r ${getLoadColor()} rounded-full`}
          />
        </div>
        {loadPercent >= 100 && (
          <p className="text-xs text-red-400 mt-1">Перегрузка! -1 к ЛОВ</p>
        )}
      </div>

      {/* Coins */}
      <div className="p-4 border-b border-gray-800">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="text-2xl">💰</span>
            <div>
              <p className="text-white font-medium">Монеты</p>
              <p className="text-xs text-gray-500">медь / серебро / золото</p>
            </div>
          </div>
          <div className="flex items-center gap-2 text-sm">
            <span className="text-amber-700">{character.copper ?? 0}м</span>
            <span className="text-gray-400">{character.silver ?? 0}с</span>
            <span className="text-amber-400">{character.gold ?? 0}з</span>
          </div>
        </div>
      </div>

      {/* Inventory list */}
      <div className="p-4">
        <h2 className="text-sm font-medium text-gray-400 mb-3">
          Предметы ({inventory.length})
        </h2>

        {inventory.length === 0 ? (
          <div className="text-center py-12">
            <span className="text-4xl block mb-4">🎒</span>
            <p className="text-gray-400">Инвентарь пуст</p>
            <p className="text-gray-500 text-sm mt-1">
              Найдите сокровища в приключениях!
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {inventory.map((item, index) => (
              <motion.div
                key={item.id || index}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.05 }}
                className="p-3 rounded-xl bg-gray-800/50 border border-gray-700/50"
              >
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-gray-700/50 flex items-center justify-center flex-shrink-0">
                    <span className="text-xl">{getItemIcon(item)}</span>
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="font-medium text-white truncate">{item.nameRu || item.name}</p>
                      {item.uses !== undefined && (
                        <span className="text-xs bg-gray-700 text-gray-300 px-1.5 py-0.5 rounded">
                          {item.uses} исп.
                        </span>
                      )}
                      {item.ammo !== undefined && (
                        <span className="text-xs bg-gray-700 text-gray-300 px-1.5 py-0.5 rounded">
                          {item.ammo} шт.
                        </span>
                      )}
                    </div>

                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                      {item.damage && (
                        <span className="text-xs text-red-400">⚔️ {item.damage}</span>
                      )}
                      {item.armor && (
                        <span className="text-xs text-blue-400">🛡️ +{item.armor}</span>
                      )}
                      <span className="text-xs text-gray-500">⚖️ {item.weight}</span>
                    </div>

                    {item.tags && item.tags.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-2">
                        {item.tags.map((tag, tagIndex) => (
                          <span
                            key={tagIndex}
                            className="text-xs px-1.5 py-0.5 rounded bg-gray-700/50 text-gray-400"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      {/* Stats summary */}
      <div className="p-4 border-t border-gray-800">
        <div className="grid grid-cols-3 gap-3">
          <div className="p-3 rounded-xl bg-red-900/30 text-center">
            <p className="text-xs text-red-400">HP</p>
            <p className="text-lg font-bold text-white">
              {character.currentHp ?? 0}/{character.maxHp ?? 0}
            </p>
          </div>
          <div className="p-3 rounded-xl bg-blue-900/30 text-center">
            <p className="text-xs text-blue-400">Броня</p>
            <p className="text-lg font-bold text-white">{character.armor ?? 0}</p>
          </div>
          <div className="p-3 rounded-xl bg-yellow-900/30 text-center">
            <p className="text-xs text-yellow-400">Монеты</p>
            <p className="text-xs font-bold text-white">
              {character.copper ?? 0}м {character.silver ?? 0}с {character.gold ?? 0}з
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
